import { useCallback } from "react";
import { ERC20_ABI } from "@/contract/abi/erc20";
import { STAKE_ABI } from "@/contract/abi/stake";
import { WrapperABI } from "@/contract/abi/wrapper";
import ContractAddress from "@/contract/addresses";
import { useGuardedWriteContract } from "@/hooks/auth/useGuardedWrite";

export type WriteHookReturn = {
  execute: () => Promise<`0x${string}`>;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  error?: Error;
  reset: () => void;
};

/**
 * Thin adapter over wagmi's `useWriteContract` that returns a stable `execute`
 * callback + the standard pending/success/error flags.
 *
 * Each concrete hook below builds the `writeContractAsync` call inline so the
 * ABI + functionName + args pair stays fully inferred by viem/wagmi — no casts.
 *
 * BUG-10: NO gas, maxFeePerGas, maxPriorityFeePerGas, gasLimit overrides.
 */

export function useApproveDnm(amountWei: bigint): WriteHookReturn {
  const { writeContractAsync, isPending, isSuccess, isError, error, reset } =
    useGuardedWriteContract();

  const execute = useCallback(
    () =>
      writeContractAsync({
        address: ContractAddress.DNM,
        abi: ERC20_ABI,
        functionName: "approve",
        args: [ContractAddress.STAKE, amountWei],
      }),
    [amountWei, writeContractAsync],
  );

  return {
    execute,
    isPending,
    isSuccess,
    isError,
    error: isError ? (error ?? undefined) : undefined,
    reset,
  };
}

export function useApproveUvm(amountWei: bigint): WriteHookReturn {
  const { writeContractAsync, isPending, isSuccess, isError, error, reset } =
    useGuardedWriteContract();

  const execute = useCallback(
    () =>
      writeContractAsync({
        address: ContractAddress.UVM,
        abi: ERC20_ABI,
        functionName: "approve",
        args: [ContractAddress.STAKE, amountWei],
      }),
    [amountWei, writeContractAsync],
  );

  return {
    execute,
    isPending,
    isSuccess,
    isError,
    error: isError ? (error ?? undefined) : undefined,
    reset,
  };
}

export function useApproveWrapper(wrapId: bigint): WriteHookReturn {
  const { writeContractAsync, isPending, isSuccess, isError, error, reset } =
    useGuardedWriteContract();

  const execute = useCallback(
    () =>
      writeContractAsync({
        address: ContractAddress.WRAPPER,
        abi: WrapperABI,
        functionName: "approve",
        args: [ContractAddress.STAKE, wrapId],
      }),
    [wrapId, writeContractAsync],
  );

  return {
    execute,
    isPending,
    isSuccess,
    isError,
    error: isError ? (error ?? undefined) : undefined,
    reset,
  };
}

export function useStake(args: {
  dnmWei: bigint;
  wrapId: bigint;
  durationSec: bigint;
}): WriteHookReturn {
  const { dnmWei, wrapId, durationSec } = args;
  const { writeContractAsync, isPending, isSuccess, isError, error, reset } =
    useGuardedWriteContract();

  const execute = useCallback(
    () =>
      writeContractAsync({
        address: ContractAddress.STAKE,
        abi: STAKE_ABI,
        functionName: "stake",
        args: [dnmWei, wrapId, durationSec],
      }),
    [dnmWei, wrapId, durationSec, writeContractAsync],
  );

  return {
    execute,
    isPending,
    isSuccess,
    isError,
    error: isError ? (error ?? undefined) : undefined,
    reset,
  };
}

export function useWithdrawReward(stakeId: bigint): WriteHookReturn {
  const { writeContractAsync, isPending, isSuccess, isError, error, reset } =
    useGuardedWriteContract();

  const execute = useCallback(
    () =>
      writeContractAsync({
        address: ContractAddress.STAKE,
        abi: STAKE_ABI,
        functionName: "withdrawReward",
        args: [stakeId],
      }),
    [stakeId, writeContractAsync],
  );

  return {
    execute,
    isPending,
    isSuccess,
    isError,
    error: isError ? (error ?? undefined) : undefined,
    reset,
  };
}

export function useWithdrawStake(args: {
  stakeId: bigint;
  claimResidual: boolean;
}): WriteHookReturn {
  const { stakeId, claimResidual } = args;
  const { writeContractAsync, isPending, isSuccess, isError, error, reset } =
    useGuardedWriteContract();

  const execute = useCallback(
    () =>
      writeContractAsync({
        address: ContractAddress.STAKE,
        abi: STAKE_ABI,
        functionName: "withdrawStake",
        args: [stakeId, claimResidual],
      }),
    [stakeId, claimResidual, writeContractAsync],
  );

  return {
    execute,
    isPending,
    isSuccess,
    isError,
    error: isError ? (error ?? undefined) : undefined,
    reset,
  };
}
